import * as z from "@-/com.npmjs.zod";

/** FSRS scheduler row attached to the meaning card. Shape mirrors `fsrs_card` table. */
const fsrsCardSliceSchema = z.object({
  difficulty: z.number(),
  due: z.string(),
  elapsedDays: z.int(),
  lapses: z.int(),
  lastReview: z.string().nullable(),
  learningSteps: z.int(),
  reps: z.int(),
  scheduledDays: z.int(),
  stability: z.number(),
  state: z.int(),
});

/**
 * Actor-DB fields the mnemonics panel needs to render on reveal. Companion to
 * `GraphemeMorphemeMnemonicsPanelMorphemeSlice` (the readonly half, sourced
 * from `staticDb` + `contentDb`). Its registered query key is invalidated when
 * any of the underlying tables mutate
 * (`user_grapheme_meaning_card`, `user_grapheme_pronunciation_card`, `user_card`, `fsrs_card`,
 * `collection`, `collection_morpheme`).
 *
 * The per-morpheme `note` lives under the info-panel dictionary section
 * and is read by its own hook, so it is deliberately excluded.
 */
export const graphemeMorphemeMnemonicsPanelMorphemeUserSliceSchema = z.object({
  /**
   * The tone-number readings the user has a pronunciation card for — the readings already
   * introduced. A review card's binder tab colours these pinyin and leaves the rest muted.
   */
  enrolledPinyinList: z.array(z.string()),

  /** Favorites collection id (when the user has created one), used by the star toggle. */
  favoritesCollectionId: z.string().nullable(),

  /** Whether this morpheme is currently a member of the favorites collection. */
  isFavorited: z.boolean(),

  /**
   * The user's meaning card for this grapheme (joined `user_card` + `fsrs_card`).
   * Null when the user has not yet started studying it. Description-section
   * reads `description` as the user's mnemonic override.
   */
  meaningCard: z
    .object({
      description: z.string().nullable(),
      fsrs: fsrsCardSliceSchema.nullable(),
      id: z.string(),
      manuallyMasteredAt: z.string().nullable(),
      startedStudyingAt: z.string().nullable(),
      userCardId: z.string(),
    })
    .nullable(),
});

export type GraphemeMorphemeMnemonicsPanelMorphemeUserSlice = z.infer<
  typeof graphemeMorphemeMnemonicsPanelMorphemeUserSliceSchema
>;
